import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import Button from "../../../componentes/button/button";
import TabelaRetornosDeEntregas from "../escala-de-rotas/TabelaRetornosDeEntregas";
import RomaneioService from "../../../services/resource/romaneioService";
import { toast } from "react-toastify";
import { capitalizeNome } from "../../../utils/reduzirNome";
import { formatLocalDate } from "../../../utils/formatDate";
import DiasTrabalhados from "../DiasTrabalhados";
import { useSearchParams } from "react-router-dom";

const fmtN = (v) => (v != null ? Number(v).toLocaleString("pt-BR") : "—");

function TabelaRomaneiosPhone() {
    const [searchParams] = useSearchParams();

    const ano = Number(searchParams.get("ano"));
    const mes = Number(searchParams.get("mes"));
    const filialId = Number(searchParams.get("filialId"));
    const funcionarioId = searchParams.get("funcionarioId");

    const romaneioService = new RomaneioService();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCarga, setSelectedCarga] = useState(null);
    const [modalRetornos, setModalRetornos] = useState(false);
    const [modalDias, setModalDias] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (ano && mes) buscarRomaneios();
    }, [ano, mes, funcionarioId]);

    const buscarRomaneios = () => {
        setLoading(true);
        romaneioService
            .listar(funcionarioId, ano, mes, filialId)
            .then((res) => setData(res.data ?? []))
            .finally(() => setLoading(false));
    };

    const filtrados = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(
            (r) =>
                String(r.id).includes(q) ||
                (r.motoristaNome ?? "").toLowerCase().includes(q) ||
                (r.placa ?? "").toLowerCase().includes(q)
        );
    }, [data, search]);

    // ── Totais ────────────────────────────────────────────────────────────────
    const totais = useMemo(
        () =>
            filtrados.reduce(
                (acc, r) => ({
                    pedidos: acc.pedidos + (Number(r.quantidadePedidos) || 0),
                    entregas: acc.entregas + (Number(r.quantidadeEntregas) || 0),
                    cubos: acc.cubos + (Number(r.cubosEntregue) || 0),
                    cubosRet: acc.cubosRet + (Number(r.cubosDevolvido) || 0),
                    caixasEnt: acc.caixasEnt + (Number(r.caixasEntregue) || 0),
                    caixasDev: acc.caixasDev + (Number(r.caixasDevolvida) || 0),
                }),
                { pedidos: 0, entregas: 0, cubos: 0, cubosRet: 0, caixasEnt: 0, caixasDev: 0 }
            ),
        [filtrados]
    );

    return (
        <div className="min-h-screen flex flex-col h-full">

            {/* ── Barra superior ── */}
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-white sticky top-0 z-10">
                <div className="relative flex-1">
                    <i className="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar romaneio, motorista, placa..."
                        className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <button
                    onClick={() => setModalDias(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition whitespace-nowrap"
                >
                    <i className="fa fa-calendar text-gray-400" />
                    Dias trabalhados
                </button>
            </div>

            {/* ── Cards (mobile-first) ── */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50">
                {loading ? (
                    <LoadingState />
                ) : filtrados.length === 0 ? (
                    <EmptyState />
                ) : (
                    filtrados.map((r) => (
                        <RomaneioCard
                            key={r.id}
                            row={r}
                            onRetornos={() => {
                                setSelectedCarga({ filialId: r.filialId, transporte: r.transporte });
                                setModalRetornos(true);
                            }}
                        />
                    ))
                )}
            </div>

            {/* ── Rodapé totais ── */}
            {filtrados.length > 0 && !loading && (
                <div className="border-t bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1.5">
                        Totais — {filtrados.length} romaneio{filtrados.length !== 1 ? "s" : ""}
                    </p>
                    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
                        {[
                            { label: "Pedidos", value: fmtN(totais.pedidos) },
                            { label: "Entregas", value: fmtN(totais.entregas) },
                            { label: "Cubagem", value: fmtN(totais.cubos) },
                            { label: "Cub. Dev.", value: fmtN(totais.cubosRet) },
                            { label: "Cxs Ent.", value: fmtN(totais.caixasEnt) },
                            { label: "Cxs Dev.", value: fmtN(totais.caixasDev) },
                        ].map((t) => (
                            <div key={t.label} className="text-center bg-gray-50 rounded-lg py-1.5 px-1">
                                <p className="text-[9px] text-gray-400 uppercase">{t.label}</p>
                                <p className="text-xs font-bold text-gray-800">{t.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Modal retornos ── */}
            <Dialog
                visible={modalRetornos}
                onHide={() => setModalRetornos(false)}
                className="w-[98%] max-w-3xl"
                blockScroll
                contentClassName="p-0"
                footer={
                    <div className="flex justify-end pt-3">
                        <Button variant="gray" label="Voltar" iconLeft="fa fa-arrow-left" onClick={() => setModalRetornos(false)} />
                    </div>
                }
            >
                <TabelaRetornosDeEntregas values={selectedCarga} />
            </Dialog>

            {/* ── Modal dias trabalhados ── */}
            <Dialog
                header="Dias Trabalhados"
                visible={modalDias}
                onHide={() => setModalDias(false)}
                className="w-[98%] max-w-xl"
                blockScroll
            >
                <DiasTrabalhados ano={ano} mes={mes} />
            </Dialog>
        </div>
    );
}

export default TabelaRomaneiosPhone;

// ─── Card individual de romaneio ──────────────────────────────────────────────
function RomaneioCard({ row, onRetornos }) {
    const [open, setOpen] = useState(false);
    const noPrazo = row.noPrazo === "No Prazo";

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Linha principal (sempre visível) */}
            <div
                className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer"
                onClick={() => setOpen((v) => !v)}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-gray-800">#{row.id}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${noPrazo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {row.noPrazo ?? "—"}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {capitalizeNome(row.motoristaNome) ?? "—"}
                        {row.placa && <span className="ml-2 text-gray-400">· {row.placa}</span>}
                    </p>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-gray-400">Emissão</p>
                    <p className="text-xs font-medium text-gray-700">{formatLocalDate(row.dataEmissao) ?? "—"}</p>
                    <i className={`fa fa-chevron-down text-gray-400 text-[10px] mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </div>

            {/* Detalhes expandidos */}
            {open && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50/60">

                    {/* Datas e equipe */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <Detail label="Data Acerto" value={formatLocalDate(row.dataAcerto)} />
                        <Detail label="Dias úteis" value={row.quantidadeDiasUteisFechamento} />
                        <Detail label="Auxiliar 1" value={capitalizeNome(row.auxiliar1Nome)} />
                        <Detail label="Auxiliar 2" value={capitalizeNome(row.auxiliar2Nome)} />
                    </div>

                    {/* Quantidades */}
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { label: "Pedidos", value: fmtN(row.quantidadePedidos) },
                            { label: "Entregas", value: fmtN(row.quantidadeEntregas) },
                            { label: "Cubagem", value: fmtN(row.cubosEntregue) },
                            { label: "Cub. Dev.", value: fmtN(row.cubosDevolvido) },
                            { label: "Cxs Ent.", value: fmtN(row.caixasEntregue) },
                            { label: "Cxs Dev.", value: fmtN(row.caixasDevolvida) },
                        ].map((item) => (
                            <div key={item.label} className="bg-white rounded-lg border border-gray-100 px-2 py-1.5 text-center">
                                <p className="text-[9px] text-gray-400 uppercase">{item.label}</p>
                                <p className="text-xs font-bold text-gray-800">{item.value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Códigos BI */}
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs">
                        <Detail label="Cód. Mot (BI)" value={row.codigoMotoristaSnapshot} />
                        <Detail label="Cód. Aux1 (BI)" value={row.codigoAjudante1Snapshot} />
                        <Detail label="Cód. Aux2 (BI)" value={row.codigoAjudante2Snapshot} />
                    </div>

                    {/* Ação retornos */}
                    {row.totalCubosRetornados > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onRetornos(); }}
                            className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg border border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 transition"
                        >
                            <i className="fa fa-undo" />
                            Ver retornos ({fmtN(row.totalCubosRetornados)} cubos)
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <span className="text-[10px] text-gray-400 uppercase block">{label}</span>
            <span className="font-medium text-gray-700">{value ?? "—"}</span>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
            <i className="fa fa-spinner fa-spin text-2xl text-teal-500" />
            <p className="text-xs">Carregando romaneios...</p>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
            <i className="fa fa-inbox text-3xl opacity-30" />
            <p className="text-xs">Nenhum romaneio encontrado.</p>
        </div>
    );
}
